import { useCallback, useEffect, useState, type RefObject } from "react";
import mapboxgl, { type Map as MapboxMap } from "mapbox-gl";
import { getBounds } from "../MapModel";
import type { PointBounds } from "services/map/MapPointQuery";

export const useMapInstance = (container: RefObject<HTMLDivElement>) => {
  const [map, setMap] = useState<MapboxMap | null>(null);
  const [bounds, setBounds] = useState<PointBounds | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tilesLoading, setTilesLoading] = useState(false);
  const [tileError, setTileError] = useState(false);
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (!container.current) return;
    let disposed = false;
    let loaded = false;
    let instance: MapboxMap | null = null;
    let timer: number | null = null;
    setMap(null);
    setBounds(null);
    setLoading(true);
    setError(false);
    setTileError(false);
    setTilesLoading(false);
    try {
      instance = new mapboxgl.Map({
        container: container.current,
        style: "mapbox://styles/orsonapikey/cl8d64cck00ab14qqzkx2zawi",
        center: [26.1062, 44.4377], zoom: 10,
      });
      instance.on("load", () => {
        if (disposed) return;
        loaded = true;
        setMap(instance);
        setBounds(getBounds(instance));
        setLoading(false);
      });
      instance.on("moveend", () => {
        if (timer !== null) window.clearTimeout(timer);
        timer = window.setTimeout(() => { if (!disposed) setBounds(getBounds(instance)); }, 120);
      });
      instance.on("dataloading", (event) => {
        // Cluster source updates are redraws, not requests for background tiles.
        if (loaded && !disposed && !("sourceId" in event && event.sourceId === "clusters")) setTilesLoading(true);
      });
      instance.on("idle", () => { if (!disposed) setTilesLoading(false); });
      instance.on("movestart", () => { if (!disposed) setTileError(false); });
      instance.on("error", () => {
        if (disposed) return;
        if (loaded) { setTileError(true); setTilesLoading(false); }
        else { setError(true); setLoading(false); }
      });
    } catch { setError(true); setLoading(false); }
    const resize = () => instance?.resize();
    window.addEventListener("resize", resize);
    return () => {
      disposed = true;
      window.removeEventListener("resize", resize);
      if (timer !== null) window.clearTimeout(timer);
      instance?.remove();
    };
  }, [container, revision]);
  return { map, bounds, loading, error, tilesLoading, tileError, retry };
};
